/**
 * OAuth app registrations a connection's `Atoms.RegistrationSelector` picks from. Callers bring their
 * own HTTP client and auth; this only knows the route, how to read the reply, and which one to preselect.
 */
import { type ServiceReply, throwIfFailedReply } from './reply';

export const REGISTRATION_INFO_PATH = '/api/oauth/registrationInfo';

export interface AppRegistration {
  id: string;
  name: string;
  isDefault: boolean;
  provider: string;
}

/** Provider → OAuth scopes, from a connection spec's `defaultState.metadata.supportedProviders`. */
export type SupportedProviders = Record<string, string[]>;

export interface AppRegistrationsRequest {
  connectorKind: string;
  supportedProviders?: SupportedProviders;
}

export type AppRegistrationsReply = ServiceReply<AppRegistration[]> | null | undefined;

export function readAppRegistrations(reply: AppRegistrationsReply): AppRegistration[] {
  throwIfFailedReply(reply);
  return Array.isArray(reply?.data) ? reply.data : [];
}

export function readSupportedProviders(
  defaultState: { metadata?: unknown } | undefined
): SupportedProviders | undefined {
  return (defaultState?.metadata as { supportedProviders?: SupportedProviders } | undefined)?.supportedProviders;
}

/**
 * What the selector writes on first load. A set value is kept; an unset one (`null`/`undefined`,
 * not `''`) takes the spec's `defaultValue`; otherwise the registration the backend marks default.
 */
export function initialAppRegistration({
  value,
  defaultValue,
  defaultProvider,
  appRegistrations
}: {
  value?: string | null;
  defaultValue?: string;
  defaultProvider?: string;
  appRegistrations: AppRegistration[];
}): { id: string; provider?: string } | undefined {
  if (value) return undefined;
  if (value == null && defaultValue) return { id: defaultValue, provider: defaultProvider };
  const fallback = appRegistrations.find(({ isDefault }) => isDefault);
  return fallback ? { id: fallback.id, provider: fallback.provider } : undefined;
}
